import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowRight, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import useAuthStore from '@/stores/authStore';
import { toast } from 'sonner';

export default function ChangePasswordPage() {
  const navigate = useNavigate();
  const { changePassword, isLoading, error, clearError } = useAuthStore();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);

  // Redirect to login if no token
  useEffect(() => {
    const token = localStorage.getItem('btccp_access_token');
    if (!token) navigate('/login', { replace: true });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    clearError();

    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newPassword || newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }

    const result = await changePassword(currentPassword, newPassword);
    if (result.success) {
      toast.success('Password changed successfully!');
      navigate('/admin', { replace: true });
    }
  };

  return (
    <div
      className="min-h-screen bg-[#06080d] flex items-center justify-center p-6"
      data-testid="change-password-page"
    >
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="wordmark text-2xl mb-2">BTCCP</h1>
          <p className="text-[#8a93a3] text-sm">Tournament Management</p>
        </div>

        <div className="bg-[#10131c] border border-[#252a37] rounded-[18px] p-8 shadow-[0_10px_30px_rgba(0,0,0,0.35)]">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-[#f0f2f7] mb-2">Change Password</h2>
            <p className="text-[#8a93a3] text-sm">
              Your account requires a password update before continuing.
            </p>
          </div>

          {error && (
            <Alert className="mb-6 bg-[rgba(239,68,68,0.1)] border-[rgba(239,68,68,0.35)]">
              <AlertCircle className="h-4 w-4 text-[#ef4444]" />
              <AlertDescription className="text-[#f0f2f7]">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-[#8a93a3] mb-2 block">
                Current Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8a93a3]" />
                <Input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="pl-10 pr-10 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
                  data-testid="change-password-current-input"
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a93a3] hover:text-[#f0f2f7]"
                >
                  {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#8a93a3] mb-2 block">
                New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8a93a3]" />
                <Input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="pl-10 pr-10 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
                  data-testid="change-password-new-input"
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#8a93a3] hover:text-[#f0f2f7]"
                >
                  {showNew ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#8a93a3] mb-2 block">
                Confirm New Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8a93a3]" />
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Repeat new password"
                  className="pl-10 h-12 bg-[#161a26] border-[#252a37] text-[#f0f2f7] placeholder:text-[#8a93a3]"
                  data-testid="change-password-confirm-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-12 bg-[#f5c518] text-[#0b0f18] font-semibold hover:bg-[#f7d24a] transition-colors mt-2"
              data-testid="change-password-submit-button"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="animate-spin h-4 w-4 border-2 border-[#0b0f18] border-t-transparent rounded-full" />
                  Updating...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Update Password
                  <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-xs text-[#8a93a3] mt-6">© Sandeep Maheshwari</p>
      </div>
    </div>
  );
}
